import * as TaskTypes from '@/@types/tasksTypes';
import { request } from './core';

/**
 * @typedef {Object} MatcherPaginatedTasksResponse
 * @property {TaskTypes.MatcherTask[]} items
 * @property {Object} pagination
 * @property {number} pagination.page
 * @property {number} pagination.pageCount
 * @property {number} pagination.pageSize
 * @property {number} pagination.rowCount
 */

/**
 * A paginated endpoint to retrieve all tasks with filterable properties
 * @param {Object} getAllMatcherTasksRequestParams
 * @param {number} getAllMatcherTasksRequestParams.page
 * @param {number} getAllMatcherTasksRequestParams.rowsPerPage
 * @param {boolean} getAllMatcherTasksRequestParams.active
 * @param {string[]} getAllMatcherTasksRequestParams.tags
 * @param {string} getAllMatcherTasksRequestParams.searchTerm
 * @param {string} getAllMatcherTasksRequestParams.searchContext
 * @returns {Promise<MatcherPaginatedTasksResponse>}
 */
export const getAllTasks = ({
  page = 1,
  rowsPerPage,
  showActive,
  tags,
  searchTerm,
  searchContext
}) =>
  request('tasks', {
    method: 'GET',
    params: {
      page,
      rowsPerPage,
      showActive,
      tags,
      searchTerm,
      searchContext
    }
  });

/**
 * Retrieves a specific task by id
 * @param {string} taskId
 * @returns {Promise<TaskTypes.MatcherTask>}
 */
export const getTask = (taskId) => request(`tasks/${taskId}`);

/**
 * The main difference between a V1 and V2 task is that a V2 task requires that a completed,
 * FULL import request id be associated with it.
 * @param {FormData} formData
 * @returns {Promise<TaskTypes.MatcherTask>}
 */
export const createTask = (formData) =>
  request('tasks', {
    method: 'POST',
    data: formData
  });

/**
 *
 * @param {Object} reRunTaskRequestParams
 * @param {string} reRunTaskRequestParams.taskId
 * @param {'match' | 're-match' | 're-run'} reRunTaskRequestParams.action
 * @param {string} reRunTaskRequestParams.resultId
 * @param {TaskTypes.MatcherTaskParams} reRunTaskRequestParams.context
 * @param {boolean} reRunTaskRequestParams.isAdvanced
 * @returns {Promise<TaskTypes.MatcherTask>}
 */
export const reRunTask = ({ taskId, action, resultId, context, isAdvanced }) =>
  request(`tasks/${taskId}`, {
    method: 'POST',
    data: {
      action,
      result_id: resultId,
      context,
      isAdvanced
    }
  });

/**
 * Reruns the Preprocessor so that the mapped_core file is used in the new run and then runs the import analysis
 * AKA: the entity import dry-run
 * @param {string} taskId
 * @returns {Promise<string>} A string indicating the preprocessor started
 */
export const startPreprocessor = (taskId) =>
  request(`tasks/${taskId}/start-preprocessor`, {
    method: 'POST'
  });

/**
 * Used to update the properties on a specified task
 * @param {string} tasksIds
 * @param {Partial<TaskTypes.MatcherTask>} taskProperties
 * @returns {Promise<TaskTypes.MatcherTask>} The updated matcher task
 */
export const updateTask = (taskId, taskProperties) =>
  request(`tasks/${taskId}`, {
    method: 'PATCH',
    data: taskProperties
  });

/**
 * Clones the specified task and the selected results, thus does not start a new Matcher task.
 * @param {Object} deepCloneTaskRequestParams
 * @param {string} deepCloneTaskRequestParams.taskId
 * @param {string} deepCloneTaskRequestParams.name
 * @param {TaskTypes.MatcherTaskBusiness} deepCloneTaskRequestParams.selectedBusiness
 * @param {string[]} deepCloneTaskRequestParams.selectedVersions
 * @returns {Promise<TaskTypes.MatcherTask>} The new matcher task
 */
export const deepCloneTask = ({
  taskId,
  name,
  selectedBusiness,
  selectedVersions
}) =>
  request(`tasks/${taskId}/deep-clone`, {
    method: 'POST',
    data: {
      name: name,
      selectedBusiness: selectedBusiness,
      selectedVersions: selectedVersions
    }
  });

/**
 * Used to update the properties on one or more tasks
 * @param {string[]} tasksIds
 * @param {object} updateProps
 * @returns {Promise<string>} A string indicating the specified tasks we're updated
 */
export const updateTasksBatch = (tasksIds, updateProps) =>
  request(`tasks/batch`, {
    method: 'PATCH',
    data: updateProps,
    params: {
      tasksIds
    }
  });

/**
 * Uploaded the generated mapped_core.csv to the static file location of the associated FULL import session
 * @param {string} taskId
 * @param {string} fileId
 * @returns {Promise<string>} A string indicating the mapped core was uploaded
 */
export const uploadMappedCore = (taskId, fileId) =>
  request(`tasks/${taskId}/upload-mapped-core`, {
    method: 'POST',
    data: {
      fileId
    }
  });
